import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/lib/auth/auth-context";
import { formatGroupNames } from "@/lib/format/group-names";
import type { ApiError } from "@/lib/api/unwrap";

import { saveConsentDecision } from "../api";

/**
 * Mandatory partner-consent step for group members (K-3).
 *
 * The old app force-redirects a group member to `member_consent_view` before
 * the questionnaire (`identification_view`), so the member always makes an
 * explicit consultation/edit consent decision. The SPA skipped this, silently
 * defaulting group members to no partner access. This gate reproduces the step:
 * the two optional accesses default off (matching the old form's current-value
 * initial for a fresh profile), and continuing persists the choice — which also
 * stamps the always-on progress baseline server-side, so the gate doesn't
 * re-prompt.
 */
export function ConsentGate({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Name the member's group(s) in the intro (K-4). Read from the already-loaded
  // /auth/me user — no extra fetch, preserving the gate's no-fetch design (K-3).
  const groupNames = user?.partner_group_names ?? [];
  const groups = formatGroupNames(groupNames, t("consents.member.and"));
  const [diagnostic, setDiagnostic] = useState(false);
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await saveConsentDecision({ diagnostic, edit });
      onDone();
    } catch (err) {
      setSaving(false);
      toast.error((err as ApiError)?.detail ?? t("consents.member.error"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("consents.member.heading")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {groupNames.length > 0 && (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>{t("consents.member.intro", { count: groupNames.length, groups })}</p>
            <p>{t("consents.member.introOptional", { groups })}</p>
          </div>
        )}
        <ul className="divide-y border-y">
          <li className="flex items-start justify-between gap-4 py-4">
            <p className="font-medium text-foreground">
              {t("consents.member.diagnostic")}
            </p>
            <Switch
              checked={diagnostic}
              onCheckedChange={setDiagnostic}
              label={t("consents.member.diagnostic")}
            />
          </li>
          <li className="flex items-start justify-between gap-4 py-4">
            <p className="font-medium text-foreground">
              {t("consents.member.edit")}
            </p>
            <Switch
              checked={edit}
              onCheckedChange={setEdit}
              label={t("consents.member.edit")}
            />
          </li>
        </ul>
        <div className="flex justify-end">
          <Button type="button" onClick={() => void submit()} disabled={saving}>
            {t("consents.member.continue")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
